import { useEffect, useRef } from "react";
import { Table as AntTable, type TableProps } from "antd";
import type { TableRef } from "antd/es/table";
import classNames from "classnames";
import { scrollToListTop } from "../../../utils/scrollToListTop";
import classes from "./Table.module.css";

/**
 * Custom styles for Akinda Table, applied only for non-mobile (desktop/tablet).
 */

export interface CustomTableProps<T> extends TableProps<T> {
  className?: string;
}

/**
 * Akinda Table component. Applies desktop/tablet custom styles only when not mobile.
 * Changing page with the built-in pager brings the table's first row back into view.
 *
 * @template T - Row data type.
 * @param props - Table props.
 */
export function Table<T extends object>({
  onChange,
  ...props
}: CustomTableProps<T>) {
  const tableRef = useRef<TableRef>(null);

  // Deleting the last row on the last page leaves a controlled `current` past
  // the end. antd only clamps the page it displays and never tells the parent,
  // which keeps fetching an empty page: report the last real page instead.
  const pagination = props.pagination || undefined;
  const current = pagination?.current;
  const total = pagination?.total;
  const pageSize = pagination?.pageSize ?? 10;
  const onPageChange = pagination?.onChange;
  useEffect(() => {
    if (!current || !total || !onPageChange) return;
    const lastPage = Math.ceil(total / pageSize);
    if (current > lastPage) onPageChange(lastPage, pageSize);
  }, [current, total, pageSize, onPageChange]);

  const handleChange: TableProps<T>["onChange"] = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    onChange?.(pagination, filters, sorter, extra);
    if (extra.action === "paginate") {
      scrollToListTop(tableRef.current?.nativeElement);
    }
  };

  return (
    <AntTable<T>
      ref={tableRef}
      bordered={false}
      {...props}
      onChange={handleChange}
      className={classNames(classes.customTable, props.className)}
    />
  );
}

export default Table;
